import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get_providers } from "../api/api-methods";
import { extractVendorListMembers, isValidProviderId } from "../utils/myVendorListsUtils";
import { mapProviderSearchItemToVendor } from "../utils/myVendorsMapper";

/**
 * Provider IDs on a list that are missing from the live My Vendors join
 * (custom-list / bookmark members never added to My Vendors).
 */
export function getMissingVendorListProviderIds(list, myVendors = []) {
  const liveIds = new Set(
    myVendors.map((vendor) => String(vendor.id || "").toLowerCase()).filter(Boolean)
  );
  return extractVendorListMembers(list)
    .map((member) => member.providerId)
    .filter((id) => isValidProviderId(id) && !liveIds.has(String(id).toLowerCase()));
}

async function fetchProvidersByIds(providerIds = []) {
  if (!providerIds.length) return [];
  const response = await get_providers.getAllProvider({
    current_page: 1,
    page_size: Math.max(providerIds.length, 1),
    provider_id_list: providerIds,
  });
  return response?.items || [];
}

/**
 * Hydrate list-only members with Discovery/Search payload (location, docs, type, services)
 * so Vendor List table rows are not empty stubs.
 */
export function useVendorListTableEnrichment(list, myVendors = [], vendorTagsMap = {}) {
  const missingIds = useMemo(
    () => getMissingVendorListProviderIds(list, myVendors),
    [list, myVendors]
  );

  const missingIdsKey = missingIds.slice().sort().join(",");

  const query = useQuery({
    queryKey: ["vendorListTableEnrichment", missingIdsKey],
    queryFn: () => fetchProvidersByIds(missingIds),
    enabled: missingIds.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const enrichmentVendors = useMemo(() => {
    const items = query.data || [];
    return items.map((item) =>
      mapProviderSearchItemToVendor(item, {
        tags: vendorTagsMap[String(item.id || item.Id || item.provider_id)] || [],
      })
    );
  }, [query.data, vendorTagsMap]);

  return {
    enrichmentVendors,
    missingIds,
    isLoading: query.isLoading && missingIds.length > 0,
    isError: query.isError,
  };
}

export default useVendorListTableEnrichment;
